<template>
  <div class="gh-sidebar__detail">
    <header class="gh-sidebar__header gh-sidebar__header--detail">
      <div class="gh-sidebar__detail-heading">
        <button type="button" class="gh-sidebar__back-icon" @click="backToList">
          <img :src="iconArrowLeft" alt="" class="gh-sidebar__back-icon-img">
        </button>
        <h2 v-if="detail" class="gh-sidebar__detail-title">
          <template v-if="serviceNameKey">{{ $t(serviceNameKey) }}
            <span class="gh-sidebar__detail-title--count">
              {{ displayCardCount }}{{ $t('generation_history.count_suffix') }}
            </span>
          </template>
        </h2>
      </div>
      <button type="button" class="gh-sidebar__close" @click="closeSidebar">
        <span>×</span>
      </button>
    </header>

    <div class="gh-sidebar__body gh-sidebar__body--detail">
      <div v-if="downloadLoading" class="gh-sidebar__download-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
      <div v-if="detailLoading && !detail" class="gh-sidebar__loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
      <div v-else-if="detailError" class="gh-sidebar__error">{{ detailError }}</div>
      <div v-else-if="detail" ref="cardsEl" class="gh-sidebar__cards">
        <component
          :is="detailCardComponent"
          v-for="(card, cardIndex) in displayCards"
          :key="getConceptDisplayCardKey(card)"
          :card="card"
          :card-index="cardIndex"
          :project-type="detail.project_type"
          :collapsed="isCardCollapsed(cardIndex)"
          :is-copied="isCopySuccess(card, 'text')"
          :is-image-copied="isCopySuccess(card, 'image')"
          :is-prompt-copied="isCopySuccess(card, 'prompt')"
          @toggle-collapse="toggleCard(cardIndex)"
          @copy="onCopyText(card)"
          @copy-image="onCopyImage(card, $event)"
          @copy-prompt="onCopyPrompt(card)"
          @download="onDownload(card, cardIndex)"
          @favorite="onToggleFavorite(card, cardIndex)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import iconArrowLeft from '@/assets/img/common/gh-icon-arrow-left.svg';
import constants from '@/shared/constants/constants';
import {
  getServiceNameKey,
  isDesignProjectType,
  expandConceptDetailCards,
  getConceptDisplayCardKey,
  getConceptCardPosterIndex,
} from '@/shared/helpers/generationHistory';
import generationHistoryCardActionsMixin from '@/shared/mixins/generationHistoryCardActionsMixin';
import GenerationHistoryDetailCardConcept from './GenerationHistoryDetailCardConcept.vue';
import GenerationHistoryDetailCardDesign from './GenerationHistoryDetailCardDesign.vue';
import GenerationHistoryDetailCardNaming from './GenerationHistoryDetailCardNaming.vue';

export default {
  name: 'GenerationHistoryDetailView',
  mixins: [generationHistoryCardActionsMixin],
  data() {
    return {
      iconArrowLeft,
      favoriteLoading: {},
      detailImageScrollTimeoutId: null,
      detailImageScrollCleanup: null,
    };
  },
  computed: {
    ...mapState('generationHistory', [
      'detail',
      'detailLoading',
      'detailError',
      'scrollAfterRefresh',
    ]),
    serviceNameKey() {
      if (!this.detail) {
        return null;
      }
      return getServiceNameKey(this.detail.project_type);
    },
    detailCardComponent() {
      if (!this.detail) {
        return GenerationHistoryDetailCardConcept;
      }
      if (isDesignProjectType(this.detail.project_type)) {
        return GenerationHistoryDetailCardDesign;
      }
      if (this.detail.project_type === constants.PROJECT_CHAT_GPT) {
        return GenerationHistoryDetailCardNaming;
      }
      return GenerationHistoryDetailCardConcept;
    },
    displayCards() {
      if (!this.detail || !this.detail.cards) {
        return [];
      }
      return expandConceptDetailCards(this.detail.cards, this.detail.project_type);
    },
    displayCardCount() {
      return this.displayCards.length;
    },
  },
  beforeDestroy() {
    // Clear pending image wait + scroll flag so leaving mid-wait does not
    // scroll the next opened detail (including a different project).
    this.teardownDetailImageScroll({ clearFlag: true });
  },
  watch: {
    detail: {
      handler() {
        this.collapsedCards = {};
        this.copiedActionKeys = {};
      },
      deep: false,
    },
    detailLoading(loading, wasLoading) {
      if (wasLoading && !loading && this.scrollAfterRefresh === 'detail-bottom') {
        if (!this.detail || this.detailError) {
          this.clearScrollAfterRefresh();
          return;
        }
        this.$nextTick(() => {
          this.scrollDetailToBottom();
        });
      }
    },
    scrollAfterRefresh(value) {
      if (value !== 'detail-bottom' || this.detailLoading || !this.detail || this.detailError) {
        return;
      }
      this.$nextTick(() => {
        this.scrollDetailToBottom();
      });
    },
  },
  methods: {
    ...mapActions('generationHistory', [
      'backToList',
      'toggleFavorite',
      'closeSidebar',
      'clearScrollAfterRefresh',
    ]),
    getConceptDisplayCardKey(card) {
      return getConceptDisplayCardKey(card);
    },
    clearDetailImageScrollTimeout() {
      if (this.detailImageScrollTimeoutId) {
        clearTimeout(this.detailImageScrollTimeoutId);
        this.detailImageScrollTimeoutId = null;
      }
    },
    teardownDetailImageScroll({ clearFlag = false } = {}) {
      this.clearDetailImageScrollTimeout();
      if (this.detailImageScrollCleanup) {
        this.detailImageScrollCleanup();
        this.detailImageScrollCleanup = null;
      }
      if (clearFlag) {
        this.clearScrollAfterRefresh();
      }
    },
    scrollDetailToBottom() {
      const run = () => {
        const cardsEl = this.$refs.cardsEl;
        if (cardsEl) {
          cardsEl.scrollTop = cardsEl.scrollHeight;
        }
      };
      this.$nextTick(() => {
        run();
        requestAnimationFrame(() => {
          run();
          this.bindDetailImageLoadScroll(run);
        });
      });
    },
    bindDetailImageLoadScroll(run) {
      const cardsEl = this.$refs.cardsEl;
      this.teardownDetailImageScroll();
      if (!cardsEl) {
        this.clearScrollAfterRefresh();
        return;
      }
      const images = cardsEl.querySelectorAll('img');
      let pending = 0;
      let finished = false;
      const boundImages = [];
      const finish = () => {
        if (finished) {
          return;
        }
        finished = true;
        this.teardownDetailImageScroll();
        this.clearScrollAfterRefresh();
      };
      const onLoadOrError = () => {
        run();
        pending -= 1;
        if (pending <= 0) {
          finish();
        }
      };
      images.forEach((img) => {
        if (img.complete) {
          return;
        }
        pending += 1;
        img.addEventListener('load', onLoadOrError);
        img.addEventListener('error', onLoadOrError);
        boundImages.push(img);
      });
      this.detailImageScrollCleanup = () => {
        boundImages.forEach((img) => {
          img.removeEventListener('load', onLoadOrError);
          img.removeEventListener('error', onLoadOrError);
        });
      };
      if (pending === 0) {
        finish();
        return;
      }
      // Safety: clear flag if some images never fire load/error.
      this.detailImageScrollTimeoutId = setTimeout(finish, 2000);
    },
    async onToggleFavorite(card, cardIndex) {
      if (this.favoriteLoading[cardIndex]) {
        return;
      }
      this.favoriteLoading[cardIndex] = true;
      try {
        await this.toggleFavorite({
          request_type: card.request_type,
          request_id: card.request_id,
          item_type: card.item_type,
          item_id: card.item_id,
          poster_index: getConceptCardPosterIndex(card),
        });
      } finally {
        this.favoriteLoading[cardIndex] = false;
      }
    },
  },
};
</script>
